import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import Skeleton from '@mui/material/Skeleton';
import { StorySectionContainer } from './styles';
import useGetPitchDeck from 'hooks/useGetPitchDeck';

/**
 * Add loading="lazy" and decoding="async" to all <img> tags in CMS HTML
 * that don't already have these attributes. This prevents the 49+ images
 * in typical pitch decks from blocking LCP. (PD-33563)
 */
function addLazyLoadingToImages(html) {
  if (!html) return html;
  return html.replace(/<img\b(?![^>]*\bloading\s*=)/gi, '<img loading="lazy" decoding="async" ');
}

/**
 * Issuer pitch HTML is authored in Froala, where section headings are routinely
 * marked up as <h1>. Rendered as-is that leaves an offering page with a dozen or
 * more h1s competing with the hero, which Semrush reports as "Multiple h1" and
 * which gives crawlers no single page topic. Demote issuer h1s to h2 so the
 * hero stays the only document-level h1. (Semrush Site Audit, 2026-08-13)
 */
export function demoteIssuerHeadings(html) {
  if (!html) return html;
  return html.replace(/<(\/?)h1(\b[^>]*)>/gi, '<$1h2$2>');
}

function prepareIssuerHtml(html) {
  return addLazyLoadingToImages(demoteIssuerHeadings(html));
}

const VIDEO_SEPARATOR = '<!--VIDEO_PLACEHOLDER-->';
const FROALA_VIDEO_PATTERN =
  /(<span[^>]*data-pasted[^>]*>\s*)?<span[^>]*class="[^"]*fr-video[^"]*"[^>]*>.*?<iframe[^>]*src="([^"]*(?:vimeo\.com|youtube\.com|youtu\.be)[^"]*)"[^>]*>.*?<\/iframe>.*?<\/span>(\s*<\/span>)?/gi;

/**
 * Split pitch deck HTML at Froala video wrappers into segments.
 * Only used on mobile — desktop renders the original HTML as-is.
 */
function splitHtmlAtVideos(html) {
  if (!html) return [];

  const videos = [];
  const markedHtml = html.replace(FROALA_VIDEO_PATTERN, (match, outerOpen, src) => {
    const cleanSrc = src.replace(/&amp;/g, '&');
    const vimeoMatch = cleanSrc.match(/player\.vimeo\.com\/video\/(\d+)/);
    const videoId = vimeoMatch ? vimeoMatch[1] : null;

    const isBackgroundVideo = cleanSrc.includes('background=1');

    videos.push({
      id: videoId,
      embedSrc: videoId ? `https://player.vimeo.com/video/${videoId}` : cleanSrc,
      thumbnailUrl: videoId ? `https://vumbnail.com/${videoId}.jpg` : null,
      isBackground: isBackgroundVideo,
      originalParams: (() => {
        try {
          const url = new URL(cleanSrc);
          return url.search;
        } catch {
          return '';
        }
      })(),
    });

    return VIDEO_SEPARATOR;
  });

  const htmlChunks = markedHtml.split(VIDEO_SEPARATOR);
  const segments = [];

  htmlChunks.forEach((chunk, i) => {
    if (chunk) {
      segments.push({ type: 'html', content: chunk });
    }
    if (i < videos.length) {
      segments.push({ type: 'video', ...videos[i] });
    }
  });

  return segments;
}

/**
 * Mobile click-to-play video. Shows thumbnail + play button.
 * Iframe created on tap — iOS Safari renders cross-origin iframe content via user gesture.
 */
function MobileVideoEmbed({
  embedSrc,
  thumbnailUrl: _thumbnailUrl,
  videoId,
  isBackground,
  originalParams,
}) {
  const [playing, setPlaying] = useState(isBackground);
  const [thumb, setThumb] = useState(null);
  const [aspectPadding, setAspectPadding] = useState(56.25);

  const handlePlay = useCallback(() => {
    setPlaying(true);
  }, []);

  // Fetch real thumbnail and aspect ratio from Vimeo oEmbed
  useEffect(() => {
    if (!videoId) return;
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=640`)
      .then((res) => res.json())
      .then((data) => {
        if (data.thumbnail_url) setThumb(data.thumbnail_url);
        if (data.width && data.height) setAspectPadding((data.height / data.width) * 100);
      })
      .catch(() => {});
  }, [videoId]);

  const isVertical = aspectPadding > 100;

  // Both thumbnail and iframe use the same maxWidth so they're the same size
  const containerMaxWidth = isVertical ? '55%' : '100%';

  if (playing) {
    return (
      <div
        style={{
          position: 'relative',
          paddingBottom: `${aspectPadding}%`,
          height: 0,
          margin: '10px auto',
          maxWidth: containerMaxWidth,
        }}
      >
        <iframe
          src={isBackground ? `${embedSrc}${originalParams}` : `${embedSrc}?autoplay=1`}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          allowFullScreen
          frameBorder="0"
          title="Video"
          referrerPolicy="no-referrer-when-downgrade"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', margin: '10px auto', maxWidth: containerMaxWidth }}>
      <div
        onClick={handlePlay}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handlePlay()}
        style={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: 8,
        }}
      >
        {thumb && (
          <img
            src={thumb}
            alt="Video thumbnail"
            loading="lazy"
            decoding="async"
            style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 8 }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 68,
            height: 48,
            background: 'rgba(0,0,0,0.7)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const StorySection = () => {
  const searchParams = useSearchParams();
  const preview = searchParams.get('preview');
  const param = useParams();
  const {
    data: pitchDeck,
    isLoading,
    error,
  } = useGetPitchDeck({
    preview,
    slug: param?.slug?.[0],
  });
  const isMobile = useIsMobile();

  // Move script logic to useEffect to prevent SSR issues
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (typeof window.docs === 'undefined') {
        window.docs = document;
      }
      if (typeof window.tickBiao === 'undefined') {
        window.tickBiao = function tickBiao() {};
      }
    }
  }, []);

  // Mobile: split HTML into segments with click-to-play video components
  const segments = useMemo(
    () => (isMobile ? splitHtmlAtVideos(pitchDeck?.html) : []),
    [pitchDeck?.html, isMobile],
  );

  // Show loading state while pitch deck is loading
  if (isLoading) {
    return (
      <StorySectionContainer data-cy="story-section">
        <div style={{ padding: '24px' }}>
          <Skeleton height={40} width="40%" style={{ marginBottom: '24px' }} />
          <Skeleton height={300} width="100%" style={{ marginBottom: '16px' }} />
          <Skeleton height={20} width="100%" style={{ marginBottom: '8px' }} />
          <Skeleton height={20} width="85%" />
        </div>
      </StorySectionContainer>
    );
  }

  // Show error state if pitch deck fails to load
  if (error) {
    return (
      <StorySectionContainer data-cy="story-section">
        <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
          <p>Unable to load company story content.</p>
        </div>
      </StorySectionContainer>
    );
  }

  if (!pitchDeck?.html) {
    return null;
  }

  // Desktop: render Froala HTML with lazy-loaded images (PD-33563)
  // Note: HTML is from trusted CMS (Froala editor) — sanitization handled server-side
  if (!isMobile) {
    return (
      <StorySectionContainer data-cy="story-section">
        <div dangerouslySetInnerHTML={{ __html: prepareIssuerHtml(pitchDeck.html) }} />
      </StorySectionContainer>
    );
  }

  // Mobile: render split segments with click-to-play video thumbnails
  return (
    <StorySectionContainer data-cy="story-section">
      {segments.map((segment, i) =>
        segment.type === 'video' ? (
          <MobileVideoEmbed
            key={`video-${i}`}
            embedSrc={segment.embedSrc}
            thumbnailUrl={segment.thumbnailUrl}
            videoId={segment.id}
            isBackground={segment.isBackground}
            originalParams={segment.originalParams}
          />
        ) : (
          <div
            key={`html-${i}`}
            dangerouslySetInnerHTML={{ __html: prepareIssuerHtml(segment.content) }}
          />
        ),
      )}
    </StorySectionContainer>
  );
};

export default StorySection;
